import {
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Description,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  Switch,
  Table,
  TableContainer,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { CalendarEvent, IBookingsFilter } from '../../../global/interfaces';
import { COLORS } from '../../../utils/colors';
import { useResultsModalStyles } from './styles';

import { useBookingsContext } from '../../../contexts/BookingsContext';
import { useCalendarStyles } from '../CalendarEvents/styles';
import { options } from '../CalendarSchedule/CalendarSideMenu';
import EventTable from '../EventsList';

interface SearchResultsModalProps {
  openFiltersModal: (value: boolean) => void;
  calView: boolean;
  setCalView: (view: boolean) => void;
  events: CalendarEvent[];
  label?: string;
  onNavigate: (
    action: 'PREV' | 'NEXT' | 'TODAY' | 'DATE',
    newDate?: Date,
  ) => void;
  periodView: string;
  setPeriodView: (value: string) => void;
}

const SearchResultsModal = ({
  calView,
  setCalView,
  events,
  label,
  onNavigate,
  periodView,
  setPeriodView,
}: SearchResultsModalProps) => {
  const calendarStyles = useCalendarStyles();
  const styles = useResultsModalStyles();
  const [labels, setLabels] = useState<string[]>([]);
  const { filters, setFilters, setBookingsFilter } = useBookingsContext();

  useEffect(() => {
    const generateFilterLabels = (filters: IBookingsFilter) => {
      const newLabels = [];

      if (filters.search) newLabels.push(`Search: ${filters.search}`);

      if (filters.sessionType && filters.sessionType.length > 0) {
        const sessionTypeLabels = filters.sessionType
          .map((type) => {
            const match = options.find((option) => option.label === type);
            return match ? match.label : type;
          })
          .join(', ');
        newLabels.push(`Session Types: ${sessionTypeLabels}`);
      }

      if (filters.region) newLabels.push(`City/Province: ${filters.region}`);
      if (filters.status) newLabels.push(`Status: ${filters.status}`);

      if (filters.startDate) {
        //const formattedStartDate = moment(filters.startDate).format(
        //  'MMMM D, YYYY',
        //);
        const formattedStartDate = format(filters.startDate, 'MMMM d, yyyy');
        newLabels.push(`Start Date: ${formattedStartDate}`);
      }

      if (filters.endDate) {
        const formattedEndDate = format(filters.endDate, 'MMMM d, yyyy');
        newLabels.push(`End Date: ${formattedEndDate}`);
      }

      if (filters.promoCode) newLabels.push('Promo Code Applied');
      return newLabels;
    };
    setLabels(generateFilterLabels(filters));
  }, [filters]);

  const handleRemoveFilter = (index: number) => {
    setLabels((prevLabels) => {
      const removedLabel = prevLabels[index];

      const updatedFilters: IBookingsFilter = { ...filters };

      if (
        removedLabel.startsWith('Start Date:') ||
        removedLabel.startsWith('End Date:')
      ) {
        const clearedFilters: IBookingsFilter = {
          startDate: null,
          endDate: null,
          search: undefined,
          region: undefined,
          promoCode: false,
          status: '',
          sessionType: [],
        };

        setFilters(clearedFilters);
        setBookingsFilter(clearedFilters);

        return [];
      } else if (removedLabel.startsWith('Search:')) {
        updatedFilters.search = undefined;
      } else if (removedLabel.startsWith('City/Province:')) {
        updatedFilters.region = undefined;
      } else if (removedLabel.startsWith('Promo Code:')) {
        updatedFilters.promoCode = false;
      } else if (removedLabel.startsWith('Status:')) {
        updatedFilters.status = '';
      } else if (removedLabel.startsWith('Session Type:')) {
        updatedFilters.sessionType = [];
      }
      setFilters(updatedFilters);
      setBookingsFilter(updatedFilters);
      return prevLabels.filter((_, i) => i !== index);
    });
  };

  const groupSessionsByDay = (sessions: CalendarEvent[]) => {
    return sessions.reduce(
      (acc, session) => {
        const day = format(session.start, 'MMMM dd, yyyy');
        if (!acc[day]) acc[day] = [];
        acc[day].push(session);
        return acc;
      },
      {} as Record<string, CalendarEvent[]>,
    );
  };

  const filterEventsByMonth = (
    events: CalendarEvent[],
    selectedMonth: string,
  ) => {
    if (!selectedMonth) return events;
    console.log(events);
    const selectedDate = new Date(selectedMonth);
    const selectedMonthIndex = selectedDate.getMonth();
    const selectedYear = selectedDate.getFullYear();

    return events.filter((event) => {
      const eventDate = new Date(event.start);
      return (
        eventDate.getMonth() === selectedMonthIndex &&
        eventDate.getFullYear() === selectedYear
      );
    });
  };

  const filteredEvents = events
    ? groupSessionsByDay(filterEventsByMonth(events, ''))
    : null;

  const listEvents = events
    ? groupSessionsByDay(filterEventsByMonth(events, label || ''))
    : null;

  return (
    <Box sx={styles.calendarContainer}>
      <Box width={'100%'} display={'flex'} alignItems={'center'}>
        <Box sx={styles.header}>
          {labels.length > 0 ? (
            <Box
              display={'flex'}
              justifyContent={'flex-start'}
              alignItems={'center'}>
              <Typography sx={styles.title}>Filter Results</Typography>
              <Box>
                {labels.map((label: string, index: number) => (
                  <Chip
                    sx={{
                      marginRight: '0.5rem',
                      marginTop: '0.5rem',
                      backgroundColor: '#fafafa',
                      '& .MuiChip-deleteIcon': {
                        backgroundColor: '#464e5f',
                        color: '#fafafa',
                        borderRadius: '1rem',
                        '&:hover': {
                          backgroundColor: '#464e5f',
                          color: '#ebebeb',
                          borderRadius: '1rem',
                        },
                      },
                    }}
                    key={index}
                    label={label}
                    onDelete={() => handleRemoveFilter(index)}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={calendarStyles.navigationContainer}>
              <Box marginRight={'auto'}>
                <Button
                  onClick={() => onNavigate('TODAY')}
                  sx={calendarStyles.today}>
                  Today
                </Button>
              </Box>
              <Box
                width={'100%'}
                display={'flex'}
                justifyContent={'flex-start'}
                alignItems={'center'}>
                <Button
                  onClick={() => onNavigate('PREV')}
                  sx={{ minWidth: '1rem', color: COLORS.DARK_GREY }}>
                  <ChevronLeft />
                </Button>
                <Button
                  onClick={() => onNavigate('NEXT')}
                  sx={{ minWidth: '1rem', color: COLORS.DARK_GREY }}>
                  <ChevronRight />
                </Button>
                <Typography
                  sx={{
                    ...calendarStyles.navigationTitle,
                  }}
                  margin={'1.375rem 1.813rem'}>
                  {label}
                </Typography>
              </Box>
            </Box>
          )}
          <Box display={'flex'} justifyContent={'flex-start'}>
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'flex-end'}>
              <CalendarMonth
                sx={{ color: calView ? COLORS.PRIMARY : '#53617a' }}
              />
              <Switch
                sx={{
                  fontSize: '13px',
                  '& .MuiSwitch-switchBase': {
                    '&.Mui-checked': {
                      color: COLORS.PRIMARY,
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: COLORS.PRIMARY,
                    },
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: COLORS.PRIMARY,
                  },
                  '& .MuiSwitch-thumb': {
                    backgroundColor: COLORS.PRIMARY,
                  },
                }}
                checked={!calView}
                onChange={() => setCalView(!calView)}
              />
              <Description
                sx={{
                  color: !calView ? COLORS.PRIMARY : '#53617a',
                  marginRight: '1rem',
                }}
              />
              <Select
                label={'Month'}
                size="small"
                sx={{
                  borderRadius: '3rem',
                  minWidth: '10rem',
                  '.MuiOutlinedInput-notchedOutline': {
                    border: '1px solid #464e5f',
                  },
                }}
                defaultValue="month"
                value={periodView}
                onChange={(e) => setPeriodView(e.target.value)}>
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="week">Week - 5 Days</MenuItem>
                <MenuItem value="day">Day</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          border: 'solid 1px #e6e6e6',
          borderRadius: '0.35rem',
          overflowY: 'auto',
        }}>
        <TableContainer>
          <Table>
            {labels.length > 0 ? (
              <EventTable filters={true} groupedEvents={filteredEvents || []} />
            ) : (
              <EventTable
                filters={false}
                groupedEvents={listEvents ? listEvents : []}
              />
            )}
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SearchResultsModal;
